'use client'

import { Fragment, useEffect, useMemo, useRef } from 'react'
import { LoadingSpinner } from '@/components/loading-spinner'
import { RestaurantCard } from '@/features/restaurants/restaurant-card/restaurant-card'
import { RestaurantCardSkeleton } from '@/features/restaurants/restaurant-card/restaurant-card-skeleton'
import { RestaurantCardsProvider } from '@/features/restaurants/restaurant-card/restaurant-cards-context'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { MobileBackButtonType, useMobileBackButtonInit } from '@/setup/back-button-init'
import { throttle } from '@/utils/functions/throttle'
import { usePreloadImage } from '@/utils/image/use-preload-image'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

export const SEARCH_RESULTS_CLASS_NAME =
    'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-x-10 xl:gap-x-12 2xl:gap-x-14 gap-y-10 md:gap-y-14 pb-14'

export const RestaurantCitySearchResults = () => {
    const {
        restaurants,
        currency,
        query: { citySlug },
        fetchNextPage,
        isFetchingNextPage,
    } = useRestaurantCitySearchContext()

    // Initialize the mobile back button on the restaurant detail page on mobile, so that it will use router.back() instead of the href
    // Reason: When users come directly to the restaurant detail page, we want to make sure that the back button takes them back to the city page and not the previous page (which could be a different website like google)
    // To achieve that, we use sessionStorage to store a flag that tells the back button to use router.back() instead of the href
    useMobileBackButtonInit(MobileBackButtonType.RESTAURANT_DETAIL)

    const containerRef = useRef<HTMLDivElement>(null)

    const onScroll = useMemo(
        () =>
            throttle(() => {
                const container = containerRef.current

                if (!container) return

                window.container = container

                const containerBottom = container.getBoundingClientRect().bottom

                const shouldFetchNextPage = containerBottom - window.innerHeight < 350

                if (shouldFetchNextPage) {
                    fetchNextPage()
                }
            }, 300),
        [fetchNextPage]
    )

    // Fetch next page when the fetchNextPageTriggerRef is in view
    // This is used to implement infinite scrolling
    useEffect(() => {
        window.addEventListener('scroll', onScroll)

        return () => {
            window.removeEventListener('scroll', onScroll)
        }
    }, [onScroll])

    // Preload gradient for restaurant detail page
    usePreloadImage('/_images/meta/fade.svg')

    return (
        <div className="w-full flex flex-col pb-14">
            <div ref={containerRef} className={cn(SEARCH_RESULTS_CLASS_NAME)}>
                <RestaurantCardsProvider styleType="default">
                    {run(() => {
                        if (!restaurants) {
                            return (
                                <Fragment>
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                    <RestaurantCardSkeleton />
                                </Fragment>
                            )
                        }

                        return restaurants?.map((restaurant, index) => {
                            return (
                                <RestaurantCard
                                    index={index}
                                    loadWithPriority
                                    citySlug={citySlug}
                                    key={'restaurant-city-search-results' + restaurant.uuid}
                                    restaurant={restaurant}
                                    currency={currency}
                                />
                            )
                        })
                    })}
                </RestaurantCardsProvider>
            </div>

            <div className="mt-4 mx-auto min-h-10">
                {isFetchingNextPage && <LoadingSpinner color={COLORS.black} size={32} />}
            </div>
        </div>
    )
}
